import React, { useRef, useState, useEffect, useMemo } from 'react';
import {
  Card, CardContent, Typography, Grid, IconButton, Button,
  Box, TextField, FormControl, InputLabel, Select, MenuItem,
  Avatar, Divider, OutlinedInput, InputAdornment,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import { Star, StarBorder, Share } from '@mui/icons-material';
import DetailItem from './DetailItem';
import ActionButtons from './ActionButtons';
import DisponibilitePicker from './DisponibilitePicker';
import { CurrencySelect } from '../Recruteur/candidat/AddTalent/CurrencySelect';
import './MissionInfo.css';
import axios from 'axios';
import debounce from 'lodash.debounce'; 

const modeOptions = ['Présentiel', 'À distance', 'Hybride'];
const urgenceOptions = ['Urgente', 'À moyen terme', 'Non urgente'];

const MissionInfo = ({
  mission, editedMission, editable, onChange, onSave, onToggleEdit,
  onToggleStatus, isRecruteurView = false, favorites = [],
  toggleFavorite, setSelectedMission, showActionButtons = false,
  entrepriseLogo
}) => {
  const data = editable ? editedMission : mission;
  const inputRef = useRef(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const [autocompleteOptions, setAutocompleteOptions] = useState([]);
  const [loadingAutocomplete, setLoadingAutocomplete] = useState(false);
  
  const fetchAutocomplete = async (input) => {
    if (!input || input.length < 2) return;
    setLoadingAutocomplete(true);
    try {
      const res = await axios.get('/api/autocomplete', { params: { input } });
      const suggestions = res.data.predictions.map(p => p.description);
      setAutocompleteOptions(suggestions);
    } catch (err) {
      console.error("Erreur lors de l'autocomplétion :", err);
    } finally {
      setLoadingAutocomplete(false);
    }
  };
  
  const debouncedFetchAutocomplete = useMemo(() => debounce(fetchAutocomplete, 300), []);
  
  useEffect(() => {
    return () => debouncedFetchAutocomplete.cancel(); 
  }, []);
  
  useEffect(() => {
    if (window.google?.maps?.places) {
      setIsMapReady(true);
    }
  }, []);

  useEffect(() => {
    if (isMapReady && inputRef.current) {
      const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current);
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        const fullAddress = place.formatted_address || place.name || '';
        onChange('lieuxTravail', fullAddress);

        const region = place.address_components?.find(comp =>
          comp.types.includes('administrative_area_level_1')
        )?.long_name;

        onChange('regionTravail', region || fullAddress);
      });
    }
  }, [isMapReady]);

  return (
    <Card className="mission-card-modern">
      <CardContent>
        <Box className="mission-header">
          <Box className="mission-header-left">
            <Avatar
              variant="rounded"
              src={entrepriseLogo ? `${process.env.REACT_APP_API_BASE_URL}${entrepriseLogo}` : undefined}
              alt={data.entreprise?.NomEntreprise || "Entreprise"}
              className="logo-avatar"
            >
              {data.entreprise?.NomEntreprise?.[0]?.toUpperCase() || "E"}
            </Avatar>
            {editable ? (
              <TextField
                label="Objet"
                value={data.nomMission}
                onChange={(e) => onChange('nomMission', e.target.value)}
                className="editInput missionTitleInput"
                fullWidth
              />
            ) : (
              <Typography variant="h5" className="mission-title">
                {data.nomMission}
              </Typography>
            )}
          </Box>

          <Box className="mission-header-right">
            {!isRecruteurView && (
              <Button
                variant="outlined"
                className={`status-btn ${data.status === 'active' ? 'green' : 'red'}`}
                onClick={onToggleStatus}
              >
                {data.status?.toUpperCase()}
              </Button>
            )}
            {isRecruteurView && (
              <>
                <IconButton onClick={() => toggleFavorite(data._id)}>
                  {favorites.includes(data._id) ? <Star color="primary" /> : <StarBorder />}
                </IconButton>
                <IconButton onClick={() => setSelectedMission(data)}>
                  <Share />
                </IconButton>
              </>
            )}
            {showActionButtons && (
              <ActionButtons
                isEditing={editable}
                onEdit={onToggleEdit}
                onSave={onSave}
                onCancel={onToggleEdit}
                showDelete={false}
                showDownload={false}
                showView={false}
              />
            )}
          </Box>
        </Box>

        <Divider className="mission-divider" />

        {editable ? (
          <TextField
            label="Contexte de la mission"
            variant="outlined"
            multiline
            minRows={3}
            fullWidth
            value={data.contexteMission}
            onChange={(e) => onChange('contexteMission', e.target.value)}
          />
        ) : (
          <DetailItem label="Contexte de la mission" value={data.contexteMission} />
        )}

        <Grid container spacing={2} className="mission-details-grid">
          <Grid item xs={12} md={6}>
            <div className="details-column">
              {editable ? (
                <>
                   <Autocomplete
                    freeSolo
                    fullWidth
                    options={autocompleteOptions}
                    loading={loadingAutocomplete}
                    value={data.lieuxTravail}
                    inputValue={data.lieuxTravail}
                    onInputChange={(e, newInput) => {
                      onChange('lieuxTravail', newInput);
                      onChange('regionTravail', newInput);
                      debouncedFetchAutocomplete(newInput);
                    }}
                    onChange={(e, value) => {
                      onChange('lieuxTravail', value || '');
                      onChange('regionTravail', value || '');
                    }}
                    noOptionsText={loadingAutocomplete ? 'Chargement…' : 'Aucun lieu trouvé'}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Lieu de travail"
                        inputRef={inputRef}
                        helperText={!isMapReady ? "⚠️ Saisie manuelle activée" : ""}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingAutocomplete && <CircularProgress size={18} />}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />

                  <TextField
                    label="Mode de travail"
                    select
                    fullWidth
                    value={data.modeTravail || ''}
                    onChange={(e) => onChange('modeTravail', e.target.value)}
                  >
                    {modeOptions.map((option) => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </TextField>

                  <FormControl fullWidth>
                    <InputLabel htmlFor="remuneration">Prime</InputLabel>
                    <OutlinedInput
                      id="remuneration"
                      type="number"
                      value={data.remuneration}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d*$/.test(value)) {
                          onChange('remuneration', value);
                        }
                      }}
                      endAdornment={
                        <InputAdornment position="end">
                          <CurrencySelect
                            value={data.devise}
                            onChange={(value) => onChange('devise', value)}
                          />
                        </InputAdornment>
                      }
                      label="Prime"
                    />
                  </FormControl>
                </>
              ) : (
                <>
                  <DetailItem label="Lieux de travail" value={data.lieuxTravail} />
                  <DetailItem label="Mode de travail" value={data.modeTravail} />
                  <DetailItem label="Prime" value={`${data.remuneration || 'N/A'} ${data.devise || ''}`} />
                </>
              )}
            </div>
          </Grid>

          <Grid item xs={12} md={6}>
            <div className="details-column">
              {editable ? (
                <>
                  <FormControl fullWidth>
                    <InputLabel>Priorité</InputLabel>
                    <Select
                      value={data.urgenceMission || ''}
                      onChange={(e) => onChange('urgenceMission', e.target.value)}
                      label="Priorité"
                    >
                      {urgenceOptions.map((option) => (
                        <MenuItem key={option} value={option}>{option}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <DisponibilitePicker
                    label="Date de démarrage"
                    value={data.dateDebut}
                    onChange={(val) => onChange('dateDebut', val)}
                  />

                  <TextField
                    label="Profil(s) recherché(s)"
                    type="number"
                    value={data.placesDispo}
                    onChange={(e) => onChange('placesDispo', e.target.value)}
                    fullWidth
                  />
                </>
              ) : (
                <>
                  <DetailItem label="Priorité" value={data.urgenceMission} />
                  <DetailItem
                    label="Date de démarrage"
                    value={data.dateDebut
                      ? new Date(data.dateDebut).toLocaleDateString('fr-FR')
                      : 'Non spécifiée'}
                  />
                  <DetailItem label="Nombre de profils" value={data.placesDispo} />
                </>
              )}
            </div>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default MissionInfo;
